import { useEffect, useRef, useState } from 'react';
import type { ComponentType, Dispatch, SetStateAction, SVGProps } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { plausibleEventClass } from '../lib/plausible';
import { useScrollDirection } from '../lib/hooks/useScrollDirection';
import { useAuth } from '../lib/auth/AuthContext';
import { useEventState } from '../lib/eventState';

type NavItem = {
  label: string;
  to: string;
  // When set, clicking the item scrolls to the element with this id on the
  // home page instead of navigating to a separate route. The `to` value is
  // still used so the link has a real href (`/#archive`) — that gives us
  // middle-click / right-click "open in new tab" behavior and lets the
  // hash-aware ScrollToTop land users on the right section when arriving
  // from another page.
  scrollTo?: string;
};

const NAV_ITEMS: NavItem[] = [
  { label: 'Play', to: '/play' },
  { label: 'Rules', to: '/rules' },
  { label: 'Watch', to: '/#archive', scrollTo: 'archive' },
  // { label: 'Players', to: '/players' },
  { label: 'Leaderboard', to: '/leaderboard' },
];

// Contest-end / Free Play (GHS5-85): Rules + Leaderboard leave the nav (rules
// stay reachable by URL; the leaderboard moves in-game). Play + Watch remain.
const CONTEST_END_HIDDEN = new Set(['/rules', '/leaderboard']);

type SocialItem = {
  label: string;
  href: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const SOCIAL_ITEMS: SocialItem[] = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/statefarm/',
    Icon: FacebookIcon,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/statefarm/',
    Icon: InstagramIcon,
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@jakefromstatefarm',
    Icon: TikTokIcon,
  },
  { label: 'X', href: 'https://x.com/StateFarm', Icon: XIcon },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/statefarm',
    Icon: YouTubeIcon,
  },
  {
    label: 'Twitch',
    href: 'https://www.twitch.tv/statefarm',
    Icon: TwitchIcon,
  },
];

/**
 * Top app bar — red gradient strip with logo (left) and nav cluster (right).
 * Desktop/tablet (lg+): full nav links + social row + account avatar.
 * Mobile (<lg): hamburger that opens a drop-down panel below the bar.
 *
 * Auth-aware:
 *   - authenticated → display name, "My Account" link, "Log out" action
 *   - anonymous    → "Log in" link to /register
 *   - loading      → "Checking session…" placeholder
 */
export default function AppBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const { pathname } = useLocation();
  const auth = useAuth();
  const { contestEnded } = useEventState();
  const navItems = contestEnded
    ? NAV_ITEMS.filter((item) => !CONTEST_END_HIDDEN.has(item.to))
    : NAV_ITEMS;
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);

  const reduceMotion = useReducedMotion();
  // Pin the bar visible whenever a menu it owns is open — sliding the bar
  // out from under an open dropdown would dismiss it mid-interaction.
  const hidden = useScrollDirection({
    disabled: menuOpen || accountOpen,
  });

  // Close the mobile menu on route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Escape closes the mobile menu and hands focus back to the toggle button.
  // Focus is only restored on Escape — when the menu closes because a link
  // was activated, focus should follow the navigation instead.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Move focus onto the first menu item when the mobile menu opens so
  // keyboard/SR users land inside the disclosure instead of behind it.
  useEffect(() => {
    if (!menuOpen) return;
    menuPanelRef.current?.querySelector<HTMLElement>('a, button')?.focus();
  }, [menuOpen]);

  return (
    <motion.nav
      role="banner"
      aria-label="Site"
      initial={false}
      animate={{ y: hidden ? '-100%' : '0%' }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { type: 'tween', duration: 0.25, ease: 'easeOut' }
      }
      className="fixed inset-x-0 top-0 z-40 bg-[linear-gradient(to_right,#ff0037_25%,#8e0f37_100%)] shadow-[0_2px_12px_rgba(255,0,55,0.35)]"
    >
      <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between gap-4 px-4 py-3 sm:px-8 sm:py-4 lg:px-12">
        <Link
          to="/"
          aria-label="State Farm Gamerhood - Home"
          className="inline-flex shrink-0 items-center"
        >
          <img
            src="/assets/GH5-logo.svg"
            alt="State Farm Gamerhood"
            width={70}
            height={35}
            className="h-7 w-auto sm:h-[35px]"
          />
        </Link>

        <div className="hidden items-center gap-6 lg:flex xl:gap-10">
          <ul className="flex items-center gap-5 font-arial text-[15px] tracking-wide text-white xl:gap-7">
            {navItems.map(({ label, to, scrollTo }) =>
              scrollTo ? (
                <li key={to}>
                  <Link
                    to={to}
                    onClick={(e) => {
                      if (pathname === '/') {
                        e.preventDefault();
                        document
                          .getElementById(scrollTo)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="transition hover:text-white/75 focus:outline-none focus-visible:underline focus-visible:underline-offset-4"
                  >
                    {label}
                  </Link>
                </li>
              ) : (
                <li key={to}>
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      `transition hover:text-white/75 focus:outline-none focus-visible:underline focus-visible:underline-offset-4 ${
                        isActive ? 'underline underline-offset-4' : ''
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                </li>
              ),
            )}
          </ul>

          <ul className="flex items-center gap-3 text-white">
            {SOCIAL_ITEMS.map(({ label, href, Icon }) => (
              <li key={label}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={`inline-flex h-6 w-6 items-center justify-center transition hover:text-white/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${plausibleEventClass(
                    'Social Click',
                    { platform: label },
                  )}`}
                >
                  <Icon className="h-full w-full" />
                </a>
              </li>
            ))}
          </ul>

          <AccountMenu open={accountOpen} setOpen={setAccountOpen} />
        </div>

        <button
          ref={menuButtonRef}
          type="button"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded text-white transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white lg:hidden"
        >
          {menuOpen ? (
            <CloseIcon className="h-6 w-6" />
          ) : (
            <HamburgerIcon className="h-7 w-7" />
          )}
        </button>
      </div>

      {menuOpen && (
        <div
          ref={menuPanelRef}
          id="mobile-menu"
          className="absolute inset-x-0 top-full border-t border-white/15 bg-brand-deep/95 shadow-xl backdrop-blur lg:hidden"
        >
          <div className="px-6 py-5">
            <ul className="flex flex-col gap-1 font-arial text-base text-white">
              {navItems.map(({ label, to, scrollTo }) =>
                scrollTo ? (
                  <li key={to}>
                    <Link
                      to={to}
                      onClick={(e) => {
                        setMenuOpen(false);
                        if (pathname === '/') {
                          e.preventDefault();
                          document
                            .getElementById(scrollTo)
                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      className="block py-2 text-white/90 transition hover:text-white/75"
                    >
                      {label}
                    </Link>
                  </li>
                ) : (
                  <li key={to}>
                    <NavLink
                      to={to}
                      className={({ isActive }) =>
                        `block py-2 transition hover:text-white/75 ${
                          isActive ? 'text-white' : 'text-white/90'
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  </li>
                ),
              )}
            </ul>

            <div className="my-4 h-px bg-white/15" />

            <ul className="flex items-center gap-5 text-white">
              {SOCIAL_ITEMS.map(({ label, href, Icon }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className={`inline-flex h-7 w-7 items-center justify-center ${plausibleEventClass(
                      'Social Click',
                      { platform: label },
                    )}`}
                  >
                    <Icon className="h-full w-full" />
                  </a>
                </li>
              ))}
            </ul>

            <div className="my-4 h-px bg-white/15" />

            <ul className="flex flex-col gap-1 font-arial text-sm text-white">
              {auth.status === 'loading' && (
                // No aria-live here — the desktop AccountMenu owns the single
                // session-status announcement so SRs don't hear it twice when
                // both menus are momentarily in the DOM during a resize.
                <li className="py-2 text-white/60">Checking session…</li>
              )}
              {auth.status === 'authenticated' && auth.user && (
                <>
                  <li className="py-2 font-heading uppercase tracking-wide text-white/70 text-xs">
                    {auth.user.displayName || auth.user.email}
                  </li>
                  <li>
                    <Link
                      to="/account"
                      onClick={() => setMenuOpen(false)}
                      className="block w-full py-2 text-left hover:text-white/75"
                    >
                      My Account
                    </Link>
                  </li>
                  {auth.user.type === 'ADMIN' && (
                    <li>
                      <Link
                        to="/admin"
                        onClick={() => setMenuOpen(false)}
                        className="block w-full py-2 text-left hover:text-white/75"
                      >
                        Admin Portal
                      </Link>
                    </li>
                  )}
                  <li>
                    <button
                      type="button"
                      className="block w-full py-2 text-left hover:text-white/75"
                      onClick={() => {
                        void auth.logout();
                        setMenuOpen(false);
                      }}
                    >
                      Log out
                    </button>
                  </li>
                </>
              )}
              {auth.status === 'anonymous' && (
                <li>
                  <Link
                    to="/register"
                    state={{ resetTo: 'landing' }}
                    onClick={() => setMenuOpen(false)}
                    className="block w-full py-2 text-left hover:text-white/75"
                  >
                    Log in
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </motion.nav>
  );
}

type AccountMenuProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function AccountMenu({ open, setOpen }: AccountMenuProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const auth = useAuth();

  useEffect(() => {
    setOpen(false);
  }, [pathname, setOpen]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    // Escape returns focus to the avatar trigger (ARIA menu pattern);
    // pointer-outside close leaves focus where the pointer put it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener('pointerdown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);

  // ARIA menu pattern: focus the first menuitem when the menu opens. The
  // loading state renders no menuitem — querySelector null-safes that.
  useEffect(() => {
    if (!open) return;
    wrapRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  const avatarClass =
    auth.status === 'loading'
      ? 'inline-flex h-9 w-9 items-center justify-center rounded-full opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent'
      : 'inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent';

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={
          auth.status === 'authenticated' && auth.user
            ? `Account menu — signed in as ${auth.user.displayName || auth.user.email}`
            : 'Account menu'
        }
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={avatarClass}
      >
        <img
          src="/assets/icons/account_circle_filled_24px.svg"
          alt=""
          aria-hidden="true"
          width={36}
          height={36}
          className="h-full w-full"
        />
      </button>

      {open && (
        <ul
          role="menu"
          aria-label="Account"
          className="absolute right-0 mt-2 w-56 overflow-hidden rounded-md border border-white/15 bg-brand-deep/95 py-1 font-arial text-sm text-white shadow-xl backdrop-blur"
        >
          {auth.status === 'loading' && (
            <li role="none" className="px-4 py-2 text-white/60">
              {/* aria-live lives on the span — role="none" strips the li's
                  semantics, so live-region props don't belong on it. */}
              <span aria-live="polite">Checking session…</span>
            </li>
          )}
          {auth.status === 'authenticated' && auth.user && (
            <>
              <li
                role="none"
                className="px-4 pt-2 pb-1 font-heading text-xs uppercase tracking-wide text-white/70"
              >
                {auth.user.displayName || auth.user.email}
              </li>
              <li role="none">
                <Link
                  role="menuitem"
                  to="/account"
                  onClick={() => setOpen(false)}
                  className="block w-full px-4 py-2 text-left hover:bg-white/10"
                >
                  My Account
                </Link>
              </li>
              {auth.user.type === 'ADMIN' && (
                <li role="none">
                  <Link
                    role="menuitem"
                    to="/admin"
                    onClick={() => setOpen(false)}
                    className="block w-full px-4 py-2 text-left hover:bg-white/10"
                  >
                    Admin Portal
                  </Link>
                </li>
              )}
              <li role="none">
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => {
                    void auth.logout();
                    setOpen(false);
                  }}
                  className="block w-full px-4 py-2 text-left hover:bg-white/10"
                >
                  Log out
                </button>
              </li>
            </>
          )}
          {auth.status === 'anonymous' && (
            <li role="none">
              <Link
                role="menuitem"
                to="/register"
                state={{ resetTo: 'landing' }}
                onClick={() => setOpen(false)}
                className="block w-full px-4 py-2 text-left hover:bg-white/10"
              >
                Log in
              </Link>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/* --- icons (brand glyphs sourced from each platform's official mark) --- */

function FacebookIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.99 3.657 9.128 8.438 9.878v-6.988h-2.54V12h2.54V9.797c0-2.507 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.772-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.99 22 12z" />
    </svg>
  );
}

function InstagramIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.336 3.608 1.311.975.975 1.249 2.242 1.311 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.336 2.633-1.311 3.608-.975.975-2.242 1.249-3.608 1.311-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.336-3.608-1.311-.975-.975-1.249-2.242-1.311-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.336-2.633 1.311-3.608.975-.975 2.242-1.249 3.608-1.311C8.416 2.175 8.796 2.163 12 2.163zM12 0C8.741 0 8.332.014 7.052.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.052.014 8.332 0 8.741 0 12s.014 3.668.072 4.948c.085 1.855.601 3.697 1.942 5.038 1.341 1.341 3.183 1.857 5.038 1.942C8.332 23.986 8.741 24 12 24s3.668-.014 4.948-.072c1.855-.085 3.697-.601 5.038-1.942 1.341-1.341 1.857-3.183 1.942-5.038.058-1.28.072-1.689.072-4.948s-.014-3.668-.072-4.948c-.085-1.855-.601-3.697-1.942-5.038C20.645.673 18.803.157 16.948.072 15.668.014 15.259 0 12 0zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.162 6.162 6.162 6.162-2.759 6.162-6.162S15.403 5.838 12 5.838zM12 16c-2.209 0-4-1.791-4-4s1.791-4 4-4 4 1.791 4 4-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.44-.645 1.44-1.44s-.645-1.44-1.44-1.44z" />
    </svg>
  );
}

function TikTokIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.07A6.33 6.33 0 0 0 5.81 20.74a6.34 6.34 0 0 0 10.86-4.43V8.66a8.17 8.17 0 0 0 4.77 1.52V6.73a4.85 4.85 0 0 1-1.85-.04z" />
    </svg>
  );
}

function XIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
  );
}

function YouTubeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

function TwitchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143L17.143 14.57h-3.428l-3 3v-3H6.857V1.714h13.714Z" />
    </svg>
  );
}

function HamburgerIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M6 6l12 12M6 18L18 6" />
    </svg>
  );
}
